/**
 * The self-lit parts of props (`buildPropGlow`: an aviation lamp, a panel lamp, a lit pane, embers, a
 * strobe), drawn in an emissive material just proud of the body. A prop with a mesh of its own gets
 * a glow of its own, which can be put out (the switch was thrown) or go with the wreck; the glows of
 * props merged into the static mesh are merged too. Owned and driven by `StaticWorld`.
 */

import { type Material, type Matrix4, Mesh, type Scene, type Texture } from 'three';
import type { Pcg32 } from '../../sim/rng';
import type { PropDef } from '../../sim/types';
import { MeshBuilder } from '../kit/MeshBuilder';
import { createEmissiveMaterial } from '../materials/toon';
import type { Palette, TimeOfDay } from '../palette';
import { buildPropGlow, type PropSwitch } from './propModels';

/** Emission on top of the lit colour: full after dark, a lift by day - the same figures the effects use. */
const BOOST_DARK = 1.2;
const BOOST_DAY = 0.45;

/** How hard anything self-lit burns at this time of day. One figure, so lamps and lit slots agree. */
export function glowBoost(timeOfDay: TimeOfDay): number {
  return timeOfDay === 'night' || timeOfDay === 'dusk' ? BOOST_DARK : BOOST_DAY;
}
/** The strike beacon's strobe: a double flash at the top of every second, `time` in seconds. */
export function strobeLit(time: number): boolean {
  const t = time - Math.floor(time);
  return t < 0.09 || (t >= 0.18 && t < 0.27);
}

export class PropGlow {
  private readonly material: Material;
  private readonly own = new Map<number, Mesh>();
  private readonly strobes = new Set<number>();
  private readonly mergedParts = new MeshBuilder();
  private merged: Mesh | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly palette: Palette,
    gradient: Texture,
    timeOfDay: TimeOfDay,
  ) {
    this.material = createEmissiveMaterial(gradient, glowBoost(timeOfDay));
  }

  /** (Re)light a prop that has its own mesh in `state`; `off`, or a kind with no lit part, leaves it dark. */
  light(prop: PropDef, at: Matrix4, rng: Pcg32, state: PropSwitch): void {
    this.douse(prop.id);
    const part = buildPropGlow(prop, this.palette, rng, state);
    if (!part) return;
    const placed = new MeshBuilder();
    placed.merge(part, at);
    const mesh = new Mesh(placed.build(), this.material);
    mesh.name = 'prop-glow';
    this.scene.add(mesh);
    this.own.set(prop.id, mesh);
    if (prop.kind === 'strikeBeacon') this.strobes.add(prop.id);
  }

  /** A prop merged into the static mesh: its lit part joins the merged glow. Call `finish` when all are in. */
  lightStatic(prop: PropDef, at: Matrix4, rng: Pcg32): void {
    const part = buildPropGlow(prop, this.palette, rng);
    if (part) this.mergedParts.merge(part, at);
  }

  finish(): void {
    if (this.merged || this.mergedParts.triangleCount === 0) return;
    this.merged = new Mesh(this.mergedParts.build(), this.material);
    this.merged.name = 'prop-glow';
    this.scene.add(this.merged);
  }

  /** Put a prop's own glow out for good: it was switched off, or destroyed. */
  douse(id: number): void {
    const mesh = this.own.get(id);
    if (!mesh) return;
    this.scene.remove(mesh);
    mesh.geometry.dispose();
    this.own.delete(id);
    this.strobes.delete(id);
  }

  /** True while prop `id` has a lit part of its own in the scene (tests, debug overlay). */
  isLit(id: number): boolean {
    return this.own.has(id);
  }

  /** Flash the strobes. `time` is the mission's running clock, seconds. */
  blink(time: number): void {
    if (this.strobes.size === 0) return;
    const lit = strobeLit(time);
    for (const id of this.strobes) {
      const mesh = this.own.get(id);
      if (mesh) mesh.visible = lit;
    }
  }

  dispose(): void {
    for (const id of [...this.own.keys()]) this.douse(id);
    if (this.merged) {
      this.scene.remove(this.merged);
      this.merged.geometry.dispose();
      this.merged = null;
    }
    this.material.dispose();
  }
}
